import { useState, useEffect } from 'react'
import { useAuth } from '../../../hooks/useAuth'
import { identityService } from '../../../shared/api'
import { Button } from '../../../components'
import { useToast } from '../../../components/ToastProvider'
import { useTenantBranding } from '../../../contexts/TenantContext.jsx'
import { Bot, AlertTriangle, Info } from 'lucide-react'

/**
 * The company's own consent to the AI Office Worker (the Office Worker's second AI gate).
 *
 * The Office Worker is an add-on. Before it sends a single request to an AI service for this
 * company, TWO things must be on: the add-on itself (the platform turns that on when the company
 * has paid for it; nobody here can change it) and THIS consent, which only this company's own
 * administrator can give or withdraw. It starts off.
 *
 * The notice text and its version come from the SERVER (routers/ai_settings.py), and the version the
 * admin read is sent back with the choice: if the notice changed while the page was open, the
 * server refuses and the page asks for a reload, so nobody consents to words they did not see.
 * Every change is written to the company's audit log by the server.
 */
function useEditableTenantId() {
  const branding = useTenantBranding()
  const id = branding?.tenantId
  return id && id !== 'master' ? id : null
}

const FIELD_LABELS = {
  business_days_waiting: 'How many business days a ticket has waited',
  business_days_since_last_request: 'Business days since the last request',
  reminders_sent_this_round: 'How many reminders were sent',
  business_days_held: 'How long a ticket has been held',
  agent_sends_this_round: 'How many emails the Office Worker sent',
  opened: 'Whether the customer opened the ticket',
  has_signer_name: 'Whether a signer name is known',
  has_reply_route: 'Whether replies have somewhere to go',
  has_address: 'Whether an email address is on file',
  delivery_problem: 'Whether an email bounced',
  rule_category: 'Why a ticket was handed to a person',
  bucket_counts: 'Counts in the morning summary',
  age_bucket_counts: 'Waiting-time counts in the morning summary',
  reason_code_counts: 'Reason counts in the morning summary',
}

export default function AiOfficeWorkerSettings() {
  const { isAdmin } = useAuth()
  const toast = useToast()
  const tenantId = useEditableTenantId()

  const [data, setData] = useState(null)
  const [consented, setConsented] = useState(false)
  const [optOuts, setOptOuts] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState(null)

  const canEdit = isAdmin && isAdmin()

  useEffect(() => {
    if (!tenantId) {
      setLoadError('These settings belong to one company. Open this page on that '
                   + "company's own site to change them.")
      setLoading(false)
      return
    }
    let cancelled = false
    identityService.getAiSettings(tenantId)
      .then(d => {
        if (cancelled) return
        setData(d)
        setConsented(Boolean(d.consented))
        setOptOuts(d.field_opt_outs || [])
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load the AI Office Worker settings', err)
        setLoadError('Could not load this setting. Refresh to try again.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [tenantId])

  const dirty = data && (consented !== Boolean(data.consented)
    || [...optOuts].sort().join(',') !== [...(data.field_opt_outs || [])].sort().join(','))

  const toggleOptOut = (field) => {
    setOptOuts(prev => prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field])
  }

  const save = async () => {
    if (!tenantId || !data) return
    setSaving(true)
    try {
      const res = await identityService.updateAiSettings(tenantId, {
        consented, notice_version: data.notice_version, free_text_opt_in: false, field_opt_outs: optOuts,
      })
      setData(res)
      setConsented(Boolean(res.consented))
      setOptOuts(res.field_opt_outs || [])
      toast.success(res.consented
        ? 'Consent saved. The Office Worker may use the AI service with placeholders only.'
        : 'Consent withdrawn. The Office Worker will use its written templates only.')
    } catch (err) {
      console.error('Failed to save the AI Office Worker settings', err)
      toast.error(err?.response?.data?.detail || 'Could not save this setting.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Bot size={18} className="text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">AI Office Worker</h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Whether your company allows the AI Office Worker to use an AI service to help word signature
        reminders and the morning summary. Without this, it still works, using its written templates.
      </p>

      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      {loadError && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {!loading && !loadError && data && (
        <>
          {!data.entitled && (
            <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <Info size={16} className="mt-0.5 shrink-0" />
              <span>
                The AI add-on is not on for your company, so no AI request is made whatever you choose
                here. Your choice is kept for when it is.
              </span>
            </div>
          )}

          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
            <p className="font-medium mb-1">What you are agreeing to</p>
            <p>{data.notice}</p>
            <p className="mt-2 text-xs text-gray-500">Notice version {data.notice_version}</p>
          </div>

          <label className="mt-3 flex items-center gap-2 text-sm text-gray-900">
            <input
              type="checkbox"
              checked={consented}
              disabled={!canEdit || saving}
              onChange={(e) => setConsented(e.target.checked)}
            />
            <span>I have read the notice, and our company agrees to it</span>
          </label>

          <details className="mt-3">
            <summary className="cursor-pointer text-sm text-gray-700">
              Facts to keep from the AI ({optOuts.length} chosen)
            </summary>
            <p className="mt-2 text-xs text-gray-600">
              Anything ticked here is never sent to the AI service, even as a number.
            </p>
            <div className="mt-2 grid gap-1 sm:grid-cols-2">
              {(data.optable_fields || []).map(field => (
                <label key={field} className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={optOuts.includes(field)}
                    disabled={!canEdit || saving}
                    onChange={() => toggleOptOut(field)}
                  />
                  <span>{FIELD_LABELS[field] || field}</span>
                </label>
              ))}
            </div>
          </details>

          {data.consent_changed_at && (
            <p className="mt-3 text-xs text-gray-500">
              Last changed {new Date(data.consent_changed_at).toLocaleString()}.
            </p>
          )}

          {canEdit && (
            <div className="mt-4 flex items-center gap-3">
              <Button variant="primary" onClick={save} disabled={saving || !dirty}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              {dirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
            </div>
          )}
          {!canEdit && (
            <p className="mt-4 text-xs text-gray-500">
              Only your company’s administrator can change this.
            </p>
          )}
        </>
      )}
    </div>
  )
}
